import React, { useState } from 'react';

import Button from '../../library/button/button';
import Link from '../../library/link/link';
import Page, { PageTitle } from '../../library/page/page';
import PhoneNumber from '../../components/phone-number/phone-number';
import DefaultLayout from '../../layouts/default-layout/default-layout';
import { CarAltSolidIcon } from '../../shared/icons/icons';

import { useFitment, useMyStore } from '../../shared/hooks/hooks';

import styles from './no-search-results-page.scss';

const SHOP_TIRES_LINKS = [
  [
    {
      title: 'All-Season Tires',
      url: '/tires/all-season-catalog?clearFitment=true'
    },
    {
      title: 'All-Terrain Tires',
      url: '/tires/all-terrain-catalog?clearFitment=true'
    },
    {
      title: 'ATV / UTV Tires',
      url: '/tires/atv-utv-catalog?clearFitment=true'
    },
    {
      title: 'Competition Tires',
      url: '/tires/competition-catalog?clearFitment=true'
    },
    {
      title: 'Mud-Terrain Tires',
      url: '/tires/mud-terrain-catalog?clearFitment=true'
    },
    {
      title: 'Passenger Tires',
      url: '/tires/passenger-catalog?clearFitment=true'
    }
  ],
  [
    {
      title: 'Performance Tires',
      url: '/tires/performance-catalog?clearFitment=true'
    },
    {
      title: 'Summer Tires',
      url: '/tires/summer-catalog?clearFitment=true'
    },
    {
      title: 'Touring Tires',
      url: '/tires/touring-catalog?clearFitment=true'
    },
    {
      title: 'Trailer Tires',
      url: '/tires/trailer-catalog?clearFitment=true'
    },
    {
      title: 'Truck / SUV Tires',
      url: '/tires/truck-catalog?clearFitment=true'
    },
    {
      title: 'Winter Tires',
      url: '/tires/winter-catalog?clearFitment=true'
    }
  ]
];

const SHOP_WHEELS_LINKS = [
  [
    {
      title: 'ATV / UTV Wheels',
      url: '/wheels/atv-utv-catalog?clearFitment=true'
    },
    {
      title: 'Chrome Wheels',
      url: '/wheels/chrome-catalog?clearFitment=true'
    },
    {
      title: 'Machined Wheels',
      url: '/wheels/machined-catalog?clearFitment=true'
    },
    {
      title: 'Mesh Wheels',
      url: '/wheels/mesh-catalog?clearFitment=true'
    },
    {
      title: 'Multi-Spoke Wheels',
      url: '/wheels/multi-spoke-catalog?clearFitment=true'
    }
  ],
  [
    {
      title: 'Painted Wheels',
      url: '/wheels/painted-catalog?clearFitment=true'
    },
    {
      title: 'Passenger Wheels',
      url: '/wheels/passenger-catalog?clearFitment=true'
    },
    {
      title: 'Trailer Wheels',
      url: '/wheels/trailer-catalog?clearFitment=true'
    },
    {
      title: 'Truck / SUV Wheels',
      url: '/wheels/truck-catalog?clearFitment=true'
    }
  ]
];

function NoSearchResultsPage() {
  const { myStore: { address: { phone } = {} } = {} } = useMyStore();
  const { selectedFitment } = useFitment();
  const { make, model, trim, year } = selectedFitment || {};
  const [isTiresColumnVisible, setTiresColumnVisible] = useState(false);
  const [isWheelsColumnVisible, setWheelsColumnVisible] = useState(false);

  function linksToColumn(links) {
    return links.map((column, idx) => (
      <div className={styles['links-column']} key={`column-${idx}`}>
        {column.map((link, idx2) => (
          <Link key={`col-${idx}-link-${idx2}`} to={link.url}>
            {link.title}
          </Link>
        ))}
      </div>
    ));
  }

  return (
    <DefaultLayout>
      <Page>
        <PageTitle className={styles['heading']} title="Search Results" />
        <div className={styles['container']}>
          <div className={styles['content']}>
            <div className={styles['no-results-text']}>
              {selectedFitment && (
                <h2 className={styles['selected-fitment']}>
                  <CarAltSolidIcon className={styles['car-icon']} />
                  {year} {make} {model} {trim}
                </h2>
              )}
              <span>
                Your search did not match any products
                {selectedFitment ? ' that fit your vehicle' : ''}, but we may
                still be able to help. Please give us a call at
              </span>
              <PhoneNumber
                className={styles['phone-number']}
                isUnformatted
                phone={phone}
              />
              <span>and we can review some more options.</span>

              {selectedFitment ? (
                <div className={styles['links']}>
                  <Link to={'/fitmentresult/tires'}>Shop tires that fit</Link>
                  <div className={styles['divider']}>|</div>
                  <Link to={'/fitmentresult/wheels'}>Shop wheels that fit</Link>
                </div>
              ) : (
                <div className={styles['links']}>
                  <Link to={'/tires'}>Shop Tires</Link>
                  <div className={styles['divider']}>|</div>
                  <Link to={'/wheels'}>Shop Wheels</Link>
                </div>
              )}
            </div>
            <div className={styles['search-section']}>
              <div className={styles['search-title']}>START A NEW SEARCH</div>
              <div className={styles['columns']}>
                <div className={styles['column']}>
                  <Button
                    className={styles['column-title']}
                    onClick={() => setTiresColumnVisible(!isTiresColumnVisible)}
                  >
                    Tires
                  </Button>
                  <div
                    className={
                      isTiresColumnVisible
                        ? styles['column-content']
                        : styles['column-content-hidden']
                    }
                  >
                    <div className={styles['search-categories']}>
                      <span>shop by</span>
                      <Button href="#/fitment/vehicle">vehicle</Button>
                      <Button href="#/fitment/tire">size</Button>
                      <Button href="/tires/brands">brand</Button>
                    </div>
                    <div className={styles['links-container']}>
                      {linksToColumn(SHOP_TIRES_LINKS)}
                    </div>
                  </div>
                </div>
                <div className={styles['column']}>
                  <Button
                    className={styles['column-title']}
                    onClick={() =>
                      setWheelsColumnVisible(!isWheelsColumnVisible)
                    }
                  >
                    Wheels
                  </Button>
                  <div
                    className={
                      isWheelsColumnVisible
                        ? styles['column-content']
                        : styles['column-content-hidden']
                    }
                  >
                    <div className={styles['search-categories']}>
                      <span>shop by</span>
                      <Button href="#/fitment/vehicle">vehicle</Button>
                      <Button href="#/fitment/wheel">size</Button>
                      <Button href="/wheels/brands">brand</Button>
                    </div>
                    <div className={styles['links-container']}>
                      {linksToColumn(SHOP_WHEELS_LINKS)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Page>
    </DefaultLayout>
  );
}

export default NoSearchResultsPage;
